import React, { useState } from "react";
import { API_URL } from "../../Redux/apiSlice";

/* endpoint */
const PAGE_CREATE_URL = `${API_URL}/pages`;

export default function PageCreate() {
  const [form, setForm] = useState({
    title: "",
    bannerImage: "",
    sections: [
      {
        heading: "",
        paragraphs: [""],
      },
    ],
    isActive: true,
  });

  const [submitting, setSubmitting] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);

  const getAuthHeader = () => {
    try {
      const raw = localStorage.getItem("knighthuntToken") || "";
      const token = raw.trim();
      if (!token) return null;
      return token.toLowerCase().startsWith("bearer ")
        ? token
        : `Bearer ${token}`;
    } catch {
      return null;
    }
  };

  /* ---------------- helpers for content editor ---------------- */

  const updateSection = (index, key, value) => {
    const sections = [...form.sections];
    sections[index][key] = value;
    setForm({ ...form, sections });
  };

  const updateParagraph = (sIndex, pIndex, value) => {
    const sections = [...form.sections];
    sections[sIndex].paragraphs[pIndex] = value;
    setForm({ ...form, sections });
  };

  const addSection = () => {
    setForm({
      ...form,
      sections: [...form.sections, { heading: "", paragraphs: [""] }],
    });
  };

  const removeSection = (index) => {
    const sections = form.sections.filter((_, i) => i !== index);
    setForm({ ...form, sections });
  };

  const addParagraph = (index) => {
    const sections = [...form.sections];
    sections[index].paragraphs.push("");
    setForm({ ...form, sections });
  };

  const removeParagraph = (sIndex, pIndex) => {
    const sections = [...form.sections];
    sections[sIndex].paragraphs.splice(pIndex, 1);
    setForm({ ...form, sections });
  };

  /* ---------------- submit ---------------- */

  const createPageRequest = async (payload) => {
    const auth = getAuthHeader();

    const resp = await fetch(PAGE_CREATE_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(auth ? { Authorization: auth } : {}),
      },
      body: JSON.stringify(payload),
      credentials: "include",
    });

    const body = await resp.json().catch(() => null);
    return { ok: resp.ok, body };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatusMsg(null);

    try {
      const payload = {
        title: form.title,
        bannerImage: form.bannerImage || null,
        content: {
          sections: form.sections.filter(
            (s) =>
              s.heading.trim() ||
              s.paragraphs.some((p) => p.trim())
          ),
        },
        isActive: form.isActive,
      };

      const result = await createPageRequest(payload);
      if (!result.ok) {
        throw new Error(result.body?.message || "Create failed");
      }

      setStatusMsg({ type: "success", text: "Page created successfully!" });
      resetForm();
    } catch (err) {
      setStatusMsg({
        type: "error",
        text: err.message || "Something went wrong.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({
      title: "",
      bannerImage: "",
      sections: [{ heading: "", paragraphs: [""] }],
      isActive: true,
    });
  };

  /* ---------------- UI ---------------- */

  return (
    <div style={{ padding: 20, paddingTop: 100, maxWidth: 900, margin: "0 auto" }}>
      {statusMsg && (
        <div
          style={{
            marginBottom: 14,
            padding: 12,
            borderRadius: 6,
            fontWeight: 500,
            background: statusMsg.type === "error" ? "#ffe5e5" : "#e6fff2",
            color: statusMsg.type === "error" ? "#b30000" : "#00663d",
          }}
        >
          {statusMsg.text}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        style={{
          padding: 20,
          borderRadius: 10,
          border: "1px solid #d7dde0",
          background: "#fafafa",
        }}
      >
        {/* Title */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600 }}>Title</label>
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
            className="form-control"
            style={{ marginTop: 6 }}
          />
        </div>

        {/* Banner Image */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontWeight: 600 }}>Banner Image URL</label>
          <input
            value={form.bannerImage}
            onChange={(e) => setForm({ ...form, bannerImage: e.target.value })}
            className="form-control"
            style={{ marginTop: 6 }}
            placeholder="https://example.com/banner.jpg"
          />
        </div>

        {/* Content Editor */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ fontWeight: 600, display: "block", marginBottom: 10 }}>
            Page Content
          </label>

          {form.sections.map((section, sIndex) => (
            <div
              key={sIndex}
              style={{
                border: "1px solid #d7dde0",
                borderRadius: 8,
                padding: 16,
                marginBottom: 16,
                background: "#fff",
              }}
            >
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500 }}>Section Heading</label>
                <input
                  value={section.heading}
                  onChange={(e) =>
                    updateSection(sIndex, "heading", e.target.value)
                  }
                  className="form-control"
                  style={{ marginTop: 6 }}
                  placeholder="Section heading"
                />
              </div>

              <label style={{ fontWeight: 500 }}>Paragraphs</label>

              {section.paragraphs.map((para, pIndex) => (
                <div key={pIndex} style={{ marginBottom: 10 }}>
                  <textarea
                    value={para}
                    onChange={(e) =>
                      updateParagraph(sIndex, pIndex, e.target.value)
                    }
                    rows={3}
                    className="form-control"
                    placeholder={`Paragraph ${pIndex + 1}`}
                    style={{ marginTop: 6 }}
                  />

                  {section.paragraphs.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger mt-2"
                      onClick={() => removeParagraph(sIndex, pIndex)}
                    >
                      Remove Paragraph
                    </button>
                  )}
                </div>
              ))}

              <button
                type="button"
                className="btn btn-sm btn-outline-secondary me-2"
                onClick={() => addParagraph(sIndex)}
              >
                + Add Paragraph
              </button>

              {form.sections.length > 1 && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => removeSection(sIndex)}
                >
                  Remove Section
                </button>
              )}
            </div>
          ))}

          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={addSection}
          >
            + Add Section
          </button>
        </div>

        {/* Active */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) =>
                setForm({ ...form, isActive: e.target.checked })
              }
              style={{ marginRight: 8 }}
            />
            Active
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting}
          className="btn btn-primary"
          style={{ padding: "10px 14px", fontWeight: 600, width: "100%" }}
        >
          {submitting ? "Saving..." : "Save Page"}
        </button>
      </form>
    </div>
  );
}
